import { Tooltip as TooltipPrimitive } from '@base-ui/react/tooltip'
import { cn } from '@/lib/utils'

function TooltipProvider({ ...props }: TooltipPrimitive.Provider.Props) {
  return <TooltipPrimitive.Provider {...props} />
}

function Tooltip({ ...props }: TooltipPrimitive.Root.Props) {
  return <TooltipPrimitive.Root data-slot="tooltip" {...props} />
}

function TooltipTrigger({ ...props }: TooltipPrimitive.Trigger.Props) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />
}

type TooltipContentProps = TooltipPrimitive.Popup.Props &
  Pick<TooltipPrimitive.Positioner.Props, 'side' | 'sideOffset' | 'align'> & {
    title?: string
  }

function TooltipContent({
  className,
  side = 'bottom',
  sideOffset = 8,
  align = 'start',
  title,
  children,
  ...props
}: TooltipContentProps) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Positioner side={side} sideOffset={sideOffset} align={align} className="isolate z-50">
        <TooltipArrow />
        <TooltipPrimitive.Popup
          data-slot="tooltip-content"
          className={cn(
            'origin-(--transform-origin) rounded-200 bg-main-surface-lv-02 px-4 py-3',
            'shadow-[4px_4px_6px_var(--color-main-elevation-lv03),-4px_-4px_6px_var(--color-main-elevation-lv03)]',
            'flex flex-col gap-1',
            'data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95',
            'data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
            'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
            'duration-100',
            className,
          )}
          {...props}
        >
          {title && <p className="text-ui-title-t2 leading-ui-title-t2 font-semibold text-main-text-lv01">{title}</p>}
          <div className="text-ui-caption-c1 leading-ui-caption-c1 text-main-text-lv02">{children}</div>
        </TooltipPrimitive.Popup>
      </TooltipPrimitive.Positioner>
    </TooltipPrimitive.Portal>
  )
}

function TooltipArrow() {
  return (
    <TooltipPrimitive.Arrow
      className={cn(
        'absolute flex items-center justify-center',
        // push arrow into the sideOffset gap (arrow height = 8px)
        'data-[side=bottom]:-top-[8px]',
        'data-[side=top]:-bottom-[8px]',
        'data-[side=left]:-right-[8px]',
        'data-[side=right]:-left-[8px]',
        'data-[side=top]:rotate-180',
        'data-[side=left]:rotate-90',
        'data-[side=right]:-rotate-90',
      )}
    >
      {/* SVG tip points up — correct for bottom tooltip; rotated for others */}
      <svg
        width="16"
        height="8"
        viewBox="0 0 16 8"
        fill="none"
        aria-hidden="true"
        className="filter-[drop-shadow(4px_4px_6px_var(--color-main-elevation-lv03))_drop-shadow(-4px_-4px_6px_var(--color-main-elevation-lv03))]"
      >
        <path d="M8 0L16 8H0L8 0Z" className="fill-main-surface-lv-02" />
      </svg>
    </TooltipPrimitive.Arrow>
  )
}

export { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger }
